import { useState, useEffect } from 'react';
import { initGA } from '../utils/analytics';

export default function CookieConsent() {
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    const consent = localStorage.getItem('voltaris_cookie_consent');
    if (!consent) {
      setIsVisible(true);
    } else {
      // Si el usuario ya había dado consentimiento previamente, comprobamos si aceptó analíticas
      try {
        const parsed = JSON.parse(consent);
        if (parsed.analytics) {
          initGA();
        }
      } catch (e) {
        console.error(e);
      }
    }

    // Escuchar eventos globales para abrir preferencias desde el Footer o enlaces externos
    const handleOpenSettings = () => setShowSettings(true);
    window.addEventListener('openCookieSettings', handleOpenSettings);

    return () => {
      window.removeEventListener('openCookieSettings', handleOpenSettings);
    };
  }, []);

  const handleAcceptAll = () => {
    const all = { necessary: true, analytics: true, marketing: true };
    localStorage.setItem('voltaris_cookie_consent', JSON.stringify(all));
    setIsVisible(false);
    setShowSettings(false);
    initGA(); // <--- Inyecta GTM al aceptar todo
  };

  const handleAcceptNecessary = () => {
    const nec = { necessary: true, analytics: false, marketing: false };
    localStorage.setItem('voltaris_cookie_consent', JSON.stringify(nec));
    setIsVisible(false);
    setShowSettings(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem('voltaris_cookie_consent', JSON.stringify(preferences));
    setIsVisible(false);
    setShowSettings(false);
    
    // Inyecta GTM solo si el usuario marcó explícitamente las analíticas
    if (preferences.analytics) {
      initGA();
    }
  };

  return (
    <>
      {isVisible && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          maxWidth: '400px',
          backgroundColor: '#ffffff',
          color: '#1e293b',
          padding: '24px',
          borderRadius: '12px',
          boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
          zIndex: 999999,
          fontFamily: 'Inter, sans-serif',
          border: '1px solid #e2e8f0'
        }}>
          <h4 style={{ margin: '0 0 10px 0', fontSize: '18px', fontWeight: '600', color: '#0f172a' }}>
            Protección de Privacidad
          </h4>
          <p style={{ margin: '0 0 16px 0', fontSize: '14px', lineHeight: '1.5', color: '#475569' }}>
            Utilizamos cookies propias y de terceros para fines analíticos y para mostrarle publicidad personalizada según sus hábitos de navegación.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <button 
              onClick={handleAcceptAll}
              style={{
                backgroundColor: '#0284c7',
                color: '#ffffff',
                border: 'none',
                padding: '10px 16px',
                borderRadius: '6px',
                fontWeight: '600',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Aceptar todas
            </button>
            <button 
              onClick={handleAcceptNecessary}
              style={{
                backgroundColor: '#f1f5f9',
                color: '#334155',
                border: 'none',
                padding: '10px 16px',
                borderRadius: '6px',
                fontWeight: '600',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Solo necesarias
            </button>
            <button 
              onClick={() => setShowSettings(true)}
              style={{
                backgroundColor: 'transparent',
                color: '#0284c7',
                border: 'none',
                padding: '6px 16px',
                cursor: 'pointer',
                fontSize: '14px',
                textDecoration: 'underline'
              }}
            >
              Gestionar preferencias
            </button>
          </div>
        </div>
      )}

      {showSettings && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000000,
          fontFamily: 'Inter, sans-serif'
        }}>
          <div style={{
            backgroundColor: '#ffffff',
            padding: '30px',
            borderRadius: '12px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ margin: '0 0 15px 0', fontSize: '20px', color: '#0f172a' }}>Configuración de Cookies</h3>
            <p style={{ fontSize: '14px', color: '#475569', marginBottom: '20px' }}>
              Personalice sus preferencias de consentimiento a continuación.
            </p>
            
            <div style={{ marginBottom: '15px', borderBottom: '1px solid #f1f5f9', paddingBottom: '15px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <strong style={{ fontSize: '14px', color: '#1e293b' }}>Necesarias</strong>
                <input type="checkbox" checked disabled style={{ cursor: 'not-allowed' }} />
              </div>
              <p style={{ fontSize: '12px', color: '#64748b', margin: '5px 0 0 0' }}>Esenciales para el funcionamiento de la web.</p>
            </div>

            <div style={{ marginBottom: '15px', borderBottom: '1px solid #f1f5f9', paddingBottom: '15px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <strong style={{ fontSize: '14px', color: '#1e293b' }}>Analíticas</strong>
                <input 
                  type="checkbox" 
                  checked={preferences.analytics} 
                  onChange={(e) => setPreferences({...preferences, analytics: e.target.checked})}
                  style={{ cursor: 'pointer' }}
                />
              </div>
              <p style={{ fontSize: '12px', color: '#64748b', margin: '5px 0 0 0' }}>Permiten analizar el tráfico y uso de la plataforma.</p>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <strong style={{ fontSize: '14px', color: '#1e293b' }}>Marketing</strong>
                <input 
                  type="checkbox" 
                  checked={preferences.marketing} 
                  onChange={(e) => setPreferences({...preferences, marketing: e.target.checked})}
                  style={{ cursor: 'pointer' }}
                />
              </div>
              <p style={{ fontSize: '12px', color: '#64748b', margin: '5px 0 0 0' }}>Utilizadas para ofrecer publicidad relevante.</p>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button 
                onClick={() => setShowSettings(false)}
                style={{
                  backgroundColor: '#f1f5f9',
                  color: '#334155',
                  border: 'none',
                  padding: '8px 14px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                Cancelar
              </button>
              <button 
                onClick={handleSavePreferences}
                style={{
                  backgroundColor: '#0284c7',
                  color: '#ffffff',
                  border: 'none',
                  padding: '8px 14px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                Guardar cambios
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}